<template>
  <mux-player v-if="video.asset && video.asset.status === 'ready'"
    class="MuxVideo"
    :playback-id="video.asset.playbackId"
    :poster="posterImageUrl"
    :disable-tracking="disableTracking"
    :metadata-video-title="trackingMetadata.videoTitle"
    :metadata-custom-1="trackingMetadata.custom1"
    :metadata-custom-2="trackingMetadata.custom2"
    accent-color="#0099FF"
    v-bind="{ autoplay }"
  />
</template>

<!--
Note on asset.filename: This field can be set from Sanity by finding the video
asset under the Videos tab in the studio and clicking the Details button. The
field is labeled "Video title or file name" and is blank by default.
-->

<script setup>
import '@mux/mux-player'

import { inject } from 'vue'
import { sanityImageUrl } from '~/lib/imageUrls.js'

function getPosterImageUrl(posterImage, sanityConfig) {
  const imageId = posterImage?.asset?._ref
  if (imageId) {
    const options = {
      w: '1280',
      auto: 'format',
    }
    return sanityImageUrl(sanityConfig, imageId, options)
  }
  return false
}

const { video, posterImage, autoplay } = defineProps({
  video: Object,
  posterImage: Object,
  autoplay: [Boolean, String],  // See https://www.mux.com/docs/guides/player-core-functionality#autoplay for possible values
})

// Note: setting the mux-player 'disable-tracking' attribute to false still
// results in tracking being disabled; only setting it to undefined works to
// keep tracking enabled.
const disableTracking = import.meta.env.DEV ? true : undefined

const trackingMetadata = {
  videoTitle: video?.asset.filename ?? `Untitled video ${video?.asset.playbackId}`,
}

if (typeof window !== 'undefined') {
  trackingMetadata.custom1 = window.location.host
  trackingMetadata.custom2 = window.location.pathname
}

const sanityConfig = inject('sanityConfig')

const posterImageUrl = getPosterImageUrl(posterImage, sanityConfig)
</script>

<style scoped>
.MuxVideo {
  --media-object-fit: cover;
  --media-object-position: center;
  --seek-backward-button: none;
  --seek-forward-button: none;
  --pip-button: none;
  display: block;
  aspect-ratio: 16 / 9;
  border-radius: var(--media-border-radius, 0);
  overflow: hidden;
}
</style>
